import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { KeyRound, Loader2, ShieldCheck, MessageCircle, Bell } from 'lucide-react';

const DEFAULT_PREFS = {
  orders: true,
  new_customer: true,
  affiliate: true,
  platform: true,
};

const NOTIF_OPTIONS = [
  {
    key: 'orders',
    title: 'Penjualan & Order',
    desc: 'Pesanan baru, pembayaran masuk, dan konfirmasi order',
  },
  {
    key: 'new_customer',
    title: 'Member Baru',
    desc: 'Notifikasi saat customer mendaftar di toko Anda',
  },
  {
    key: 'affiliate',
    title: 'Affiliate Toko',
    desc: 'Affiliate baru mendaftar atau mendapat komisi',
  },
  {
    key: 'platform',
    title: 'Notifikasi TokoMoe',
    desc: 'Subscription expired, perpanjangan, dan pengingat dari platform',
  },
];

export default function TenantAccountSettings() {
  const { user } = useAuth();
  const isOwner = ['tenant_owner', 'tenant'].includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [whatsapp, setWhatsapp] = useState('');
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (!isOwner) {
      setLoading(false);
      return;
    }
    api.get('/tenant/account')
      .then((res) => {
        setAccount(res.data);
        setWhatsapp(res.data.whatsapp || res.data.phone || '');
        setPrefs({ ...DEFAULT_PREFS, ...(res.data.notification_prefs || {}) });
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Gagal memuat pengaturan akun');
      })
      .finally(() => setLoading(false));
  }, [isOwner]);

  const handleSaveAccount = async () => {
    if (!whatsapp.trim()) {
      return toast.error('Nomor WhatsApp wajib diisi untuk menerima notifikasi');
    }
    setSaving(true);
    try {
      const res = await api.put('/tenant/account', {
        whatsapp: whatsapp.trim(),
        phone: whatsapp.trim(),
        notification_prefs: prefs,
      });
      setPrefs(res.data.notification_prefs || prefs);
      toast.success('Pengaturan akun berhasil disimpan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!pw.current_password || !pw.new_password) {
      return toast.error('Lengkapi semua field password');
    }
    if (pw.new_password.length < 6) {
      return toast.error('Password baru minimal 6 karakter');
    }
    if (pw.new_password !== pw.confirm_password) {
      return toast.error('Konfirmasi password tidak cocok');
    }
    setSavingPw(true);
    try {
      await api.put('/auth/me/password', {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success('Password berhasil diubah');
      setPw({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setSavingPw(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D6CDF]" />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <ShieldCheck size={40} className="mx-auto text-slate-300 mb-3" />
        <h2 className="text-lg font-semibold text-slate-900">Akun Pemilik Toko</h2>
        <p className="text-sm text-slate-500 mt-2">
          Pengaturan WhatsApp dan notifikasi hanya dapat diubah oleh pemilik toko.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="tenant-account-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Akun Saya
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Kelola nomor WhatsApp pemilik toko untuk menerima notifikasi penting.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#2D6CDF]" /> Informasi Akun
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-slate-500">Nama</p>
              <p className="font-medium text-slate-900">{account?.name || user?.name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Email</p>
              <p className="font-medium text-slate-900 break-all">{account?.email || user?.email || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Toko</p>
              <p className="font-medium text-slate-900">{account?.tenant_name || '-'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircle size={18} className="text-emerald-600" /> WhatsApp Pemilik
            </CardTitle>
            <CardDescription>
              Notifikasi order, member baru, affiliate, dan info subscription TokoMoe akan dikirim ke nomor ini.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label htmlFor="owner-wa">Nomor WhatsApp</Label>
              <Input
                id="owner-wa"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="628123456789"
                className="mt-1 font-mono"
                data-testid="owner-whatsapp-input"
              />
              <p className="text-xs text-slate-500 mt-1">Format internasional tanpa +, contoh: 628123456789</p>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Bell size={16} className="text-[#2D6CDF]" />
                <p className="text-sm font-semibold text-slate-900">Preferensi Notifikasi WA</p>
              </div>
              <div className="space-y-3">
                {NOTIF_OPTIONS.map((opt) => (
                  <div key={opt.key} className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-100 bg-slate-50/50">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{opt.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                    </div>
                    <Switch
                      checked={!!prefs[opt.key]}
                      onCheckedChange={(v) => setPrefs((p) => ({ ...p, [opt.key]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleSaveAccount} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-account-btn">
              {saving && <Loader2 size={16} className="animate-spin mr-2" />}
              Simpan Pengaturan
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound size={18} className="text-violet-500" /> Ubah Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="curr-pw">Password Saat Ini</Label>
                <Input
                  id="curr-pw"
                  type="password"
                  autoComplete="current-password"
                  value={pw.current_password}
                  onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-pw">Password Baru</Label>
                <Input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={pw.new_password}
                  onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
                  placeholder="Minimal 6 karakter"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="conf-pw">Konfirmasi Password Baru</Label>
                <Input
                  id="conf-pw"
                  type="password"
                  autoComplete="new-password"
                  value={pw.confirm_password}
                  onChange={(e) => setPw((p) => ({ ...p, confirm_password: e.target.value }))}
                  className="mt-1"
                  required
                />
              </div>
              <Button type="submit" variant="outline" disabled={savingPw}>
                {savingPw && <Loader2 size={16} className="animate-spin mr-2" />}
                Ubah Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
